import React, { useState, useRef, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMapEvents,
  LayersControl,
  Popup,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const LoadingIcon = L.divIcon({
  html: `<div class="leaflet-loading-icon"></div>`,
  className: "",
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const reverseGeocode = async (lat, lng) => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&addressdetails=1`,
      {
        headers: {
          "Accept-Language": "en",
        },
      }
    );
    if (!res.ok) return null;
    const data = await res.json();
    return data.address || null;
  } catch (error) {
    console.error("Reverse geocoding failed:", error);
    return null;
  }
};

const LocationPicker = ({ positions, setPositions, setLoadingPosition }) => {
  useMapEvents({
    async click(e) {
      const { lat, lng } = e.latlng;
      setLoadingPosition({ lat, lng });
      const address = await reverseGeocode(lat, lng);
      setPositions((prev) => [...prev, { lat, lng, address }]);
      setLoadingPosition(null);
    },
  });

  return (
    <>
      {positions.map((pos, idx) => (
        <Marker
          key={idx}
          position={[pos.lat, pos.lng]}
          eventHandlers={{
            click: () => {
              setPositions((prev) => prev.filter((_, i) => i !== idx));
            },
          }}
        />
      ))}
    </>
  );
};

const MapDialog = ({ visible, onHide, onSave, initialValue }) => {
  const [positions, setPositions] = useState(initialValue || []);
  const [latInput, setLatInput] = useState("");
  const [lngInput, setLngInput] = useState("");
  const [coordError, setCoordError] = useState("");
  const [loadingPosition, setLoadingPosition] = useState(null);

  // Place search (navigation only)
  const [placeQuery, setPlaceQuery] = useState("");
  const [placeResults, setPlaceResults] = useState([]);
  const [placeLoading, setPlaceLoading] = useState(false);
  const [placeError, setPlaceError] = useState("");
  const searchSeq = useRef(0);

  // Store a selected place if chosen before map ready
  const pendingCenter = useRef(null);
  const mapRef = useRef(null);

  // If parent passes initial positions later
  useEffect(() => {
    if (
      Array.isArray(initialValue) &&
      initialValue.length &&
      positions.length === 0
    ) {
      setPositions(initialValue);
    }
  }, [initialValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Recalculate map size when dialog opens (important for Leaflet centering)
  useEffect(() => {
    if (visible && mapRef.current) {
      setTimeout(() => {
        mapRef.current.invalidateSize();
        // Apply any pending center after size fix
        if (pendingCenter.current) {
          const { lat, lng, zoom } = pendingCenter.current;
          mapRef.current.setView([lat, lng], zoom);
          pendingCenter.current = null;
        }
      }, 120);
    }
  }, [visible]);

  // Debounced place search (Nominatim)
  useEffect(() => {
    const q = placeQuery.trim();
    if (!q) {
      setPlaceResults([]);
      setPlaceError("");
      setPlaceLoading(false);
      return;
    }
    const seq = ++searchSeq.current;
    setPlaceLoading(true);
    setPlaceError("");
    const timeoutId = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&limit=8&addressdetails=1&q=${encodeURIComponent(
            q
          )}`,
          {
            headers: {
              "Accept-Language": "en",
            },
          }
        );
        if (!res.ok) throw new Error("Search failed");
        const data = await res.json();
        if (searchSeq.current === seq) {
          setPlaceResults(data);
          setPlaceError(data.length ? "" : "No results");
        }
      } catch (_) {
        if (searchSeq.current === seq) {
          setPlaceResults([]);
          setPlaceError("No results");
        }
      } finally {
        if (searchSeq.current === seq) {
          setPlaceLoading(false);
        }
      }
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [placeQuery]);

  const selectPlace = (r) => {
    // Center map only (no marker added)
    const lat = parseFloat(r.lat);
    const lng = parseFloat(r.lon);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;

    const targetZoom = 10;
    const map = mapRef.current;

    // Clear search results first to prevent interference
    setPlaceResults([]);
    setPlaceQuery(r.display_name.split(",")[0]); // Use only the main place name

    if (!map) {
      pendingCenter.current = { lat, lng, zoom: targetZoom };
    } else {
      // Use flyTo for smooth animation
      map.flyTo([lat, lng], targetZoom, {
        duration: 1.5,
        easeLinearity: 0.1,
      });
    }
  };

  const validateAndAdd = async () => {
    setCoordError("");
    const lat = parseFloat(latInput.trim());
    const lng = parseFloat(lngInput.trim());
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      setCoordError("Latitude and Longitude must be numbers.");
      return;
    }
    if (lat < -90 || lat > 90) {
      setCoordError("Latitude must be between -90 and 90.");
      return;
    }
    if (lng < -180 || lng > 180) {
      setCoordError("Longitude must be between -180 and 180.");
      return;
    }
    const exists = positions.some(
      (p) => Math.abs(p.lat - lat) < 1e-6 && Math.abs(p.lng - lng) < 1e-6
    );
    if (exists) {
      setCoordError("These coordinates are already added.");
      return;
    }

    const address = await reverseGeocode(lat, lng);
    const newPos = { lat, lng, address };

    setPositions((prev) => [...prev, newPos]);
    if (mapRef.current) {
      mapRef.current.setView(
        [newPos.lat, newPos.lng],
        mapRef.current.getZoom() || 15
      );
    }
    setLatInput("");
    setLngInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") validateAndAdd();
  };

  const removePosition = (index) => {
    setPositions((prev) => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => setPositions([]);

  return (
    <Dialog
      header="Select Coordinates (You Can Select Multiple Points)"
      visible={visible}
      style={{ width: "95vw", height: "95%" }}
      modal
      onHide={onHide}
    >
      {/* Main Controls Container */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "0.75rem",
          gap: "1rem",
        }}
      >
        {/* Place Search (Left Side) */}
        <div>
          <label style={{ fontSize: "0.75rem", display: "block" }}>
            Search place (like Google Maps)
          </label>
          <div style={{ position: "relative", width: 350 }}>
            <InputText
              value={placeQuery}
              onChange={(e) => setPlaceQuery(e.target.value)}
              placeholder="Type a place name..."
              style={{ width: "100%", paddingRight: "2.2rem" }}
            />
            {placeLoading && (
              <span
                style={{
                  position: "absolute",
                  right: "0.6rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  fontSize: "0.7rem",
                  opacity: 0.6,
                }}
              >
                ...
              </span>
            )}
            {placeResults.length > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  background: "#fff",
                  border: "1px solid #d0d0d0",
                  borderTop: "none",
                  zIndex: 1000,
                  maxHeight: "230px",
                  overflowY: "auto",
                  boxShadow: "0 3px 6px rgba(0,0,0,0.15)",
                }}
              >
                {placeResults.map((r) => (
                  <div
                    key={r.place_id}
                    onClick={() => selectPlace(r)}
                    style={{
                      padding: "0.4rem 0.55rem",
                      cursor: "pointer",
                      fontSize: "0.72rem",
                      lineHeight: 1.2,
                      borderBottom: "1px solid #f0f0f0",
                    }}
                    onMouseDown={(e) => e.preventDefault()} // keep input focus
                  >
                    <strong>{r.display_name.split(",")[0]}</strong>
                    <div style={{ opacity: 0.7 }}>
                      {r.display_name.replace(r.display_name.split(",")[0], "")}
                    </div>
                  </div>
                ))}
              </div>
            )}
            {placeError && !placeLoading && placeResults.length === 0 && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  background: "#fff",
                  border: "1px solid #e0b4b4",
                  color: "#a40000",
                  fontSize: "0.7rem",
                  padding: "0.4rem 0.55rem",
                  zIndex: 1000,
                }}
              >
                {placeError}
              </div>
            )}
          </div>
          <div
            style={{
              fontSize: "0.6rem",
              opacity: 0.55,
              marginTop: "0.25rem",
              maxWidth: 350,
            }}
          >
            Click a result to center the map (no marker added).
          </div>
        </div>

        {/* Coordinate Inputs (Right Side) */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            alignItems: "flex-start",
            justifyContent: "flex-end",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ fontSize: "0.75rem" }}>Latitude</label>
            <InputText
              value={latInput}
              onChange={(e) => setLatInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. 36.5"
              style={{ width: "140px" }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <label style={{ fontSize: "0.75rem" }}>Longitude</label>
            <InputText
              value={lngInput}
              onChange={(e) => setLngInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="e.g. 8.78"
              style={{ width: "140px" }}
            />
          </div>
          <Button
            label="Add"
            onClick={validateAndAdd}
            style={{
              backgroundColor: "#0d2e4d",
              border: "none",
              height: "fit-content",
              marginTop: "1.1rem",
            }}
          />
          {positions.length > 0 && (
            <Button
              label="Clear All"
              className="p-button-text"
              onClick={clearAll}
              style={{
                color: "#b30000",
                height: "fit-content",
                marginTop: "1.1rem",
              }}
            />
          )}
          {coordError && (
            <div
              style={{
                color: "crimson",
                fontSize: "0.75rem",
                flexBasis: "100%",
                marginTop: "-0.25rem",
                textAlign: "right",
              }}
            >
              {coordError}
            </div>
          )}
          <div
            style={{
              fontSize: "0.65rem",
              opacity: 0.7,
              flexBasis: "100%",
              marginTop: coordError ? "0.15rem" : "-0.25rem",
              textAlign: "right",
            }}
          >
            Tip: Click map to add a point, click marker to remove.
          </div>
        </div>
      </div>

      {/* Map */}
      <div style={{ height: "64%", width: "100%", marginBottom: "0.75rem" }}>
        <MapContainer
          center={
            positions.length > 0
              ? [positions[0].lat, positions[0].lng]
              : [36.5002453, 8.7813287]
          }
          zoom={10}
          style={{ height: "100%", width: "100%" }}
          ref={mapRef}
        >
          <LayersControl position="topright">
            <LayersControl.BaseLayer checked name="Streets">
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
            </LayersControl.BaseLayer>
            <LayersControl.BaseLayer name="Satellite">
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution="Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community"
              />
            </LayersControl.BaseLayer>
          </LayersControl>
          <LocationPicker
            positions={positions}
            setPositions={setPositions}
            setLoadingPosition={setLoadingPosition}
          />
          {loadingPosition && (
            <Marker
              position={[loadingPosition.lat, loadingPosition.lng]}
              icon={LoadingIcon}
            >
              <Popup>Fetching address...</Popup>
            </Marker>
          )}
        </MapContainer>
      </div>

      {/* List of positions */}
      <div
        style={{
          maxHeight: "20%",
          overflowY: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: "4px",
          padding: "0.5rem",
          marginBottom: "0.75rem",
          background: "#fafafa",
        }}
      >
        {positions.length === 0 && (
          <div style={{ fontSize: "0.8rem", opacity: 0.6 }}>
            No positions selected yet.
          </div>
        )}
        {positions.map((pos, i) => (
          <div
            key={`${pos.lat}-${pos.lng}-${i}`}
            style={{
              padding: "0.4rem 0.5rem",
              background: i % 2 === 0 ? "#ffffff" : "#f2f6f8",
              borderRadius: "3px",
              marginBottom: "0.25rem",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontSize: "0.8rem",
                fontWeight: "bold",
              }}
            >
              <div>
                #{i + 1} | Lat: {pos.lat.toFixed(5)}, Lng: {pos.lng.toFixed(5)}
              </div>
              <Button
                icon="pi pi-times"
                className="p-button-rounded p-button-text p-button-danger"
                onClick={() => removePosition(i)}
                style={{ height: "1.6rem", width: "1.6rem" }}
                aria-label="Remove"
              />
            </div>
            {pos.address && (
              <div
                style={{
                  fontSize: "0.7rem",
                  opacity: 0.8,
                  marginTop: "0.25rem",
                  paddingLeft: "0.2rem",
                }}
              >
                {Object.values(pos.address).join(", ")}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Actions */}
      <div style={{ textAlign: "right" }}>
        <Button
          label="Cancel"
          className="p-button-text"
          onClick={onHide}
          style={{
            backgroundColor: "#d97a07",
            border: "none",
            color: "#fff",
          }}
        />
        <Button
          label="Save"
          onClick={() => {
            if (positions.length > 0) {
              onSave(positions);
            }
            onHide();
          }}
          disabled={positions.length === 0}
          style={{
            backgroundColor: "#0d2e4d",
            border: "none",
            marginLeft: "0.5rem",
          }}
        />
      </div>
    </Dialog>
  );
};

export default MapDialog;
